/**
 * Presenting a note as slides: in the page (the side bars stay, or hide them with one button), or
 * full screen. Arrow keys, Space and a click move on; Esc leaves; O shows every slide; N the speaker
 * notes; F full screen; P prints (or saves as PDF) one slide per page.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, LayoutGrid, Maximize, Minimize, PanelLeftClose, PanelLeftOpen, Printer, StickyNote, X } from "lucide-react";
import { useUi } from "@/lib/store";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { MarkdownPreview } from "@/features/notes/MarkdownPreview";
import { splitSlides, type Slide } from "@brainlog/shared";

/** Slides are laid out at this size, then scaled to the room available (16:9). */
const W = 1280;
const H = 720;

/** A slide at its real size; the title slide (the first, starting with a # title) is centred. */
function SlideCanvas({ slide, first }: { slide: Slide; first: boolean }) {
  const titleSlide = first && /^#\s/.test(slide.md);
  return (
    <div className={cn("slide-canvas flex h-full w-full flex-col overflow-hidden bg-card px-24 py-16 text-card-foreground", titleSlide && "justify-center text-center")} style={{ width: W, height: H }}>
      <MarkdownPreview markdown={slide.md} className={cn("slide-md", titleSlide && "slide-title")} />
    </div>
  );
}

/** Fits a slide into its box. */
function Scaled({ children, className }: { children: React.ReactNode; className?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const fit = () => setScale(Math.min(el.clientWidth / W, el.clientHeight / H));
    fit();
    const ro = new ResizeObserver(() => requestAnimationFrame(fit));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={box} className={cn("relative flex min-h-0 min-w-0 items-center justify-center overflow-hidden", className)}>
      <div className="shrink-0 shadow-lg ring-1 ring-border" style={{ width: W * scale, height: H * scale }}>
        <div style={{ width: W, height: H, transform: `scale(${scale})`, transformOrigin: "top left" }}>{scale > 0 && children}</div>
      </div>
    </div>
  );
}

export function SlideDeck({ markdown, title, onClose }: { markdown: string; title: string; onClose(): void }) {
  const slides = useMemo(() => splitSlides(markdown), [markdown]);
  const [index, setIndex] = useState(0);
  const [overview, setOverview] = useState(false);
  const [notesShown, setNotesShown] = useState(true);
  const [full, setFull] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [idle, setIdle] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const focusMode = useUi((s) => s.focusMode);
  const count = slides.length;
  const at = Math.min(index, Math.max(0, count - 1));
  const slide = slides[at];

  const go = useCallback((n: number) => setIndex(() => Math.max(0, Math.min(count - 1, n))), [count]);
  const toggleFull = useCallback(async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await root.current?.requestFullscreen();
    } catch {
      /* not allowed here (e.g. in a frame): stay in the page */
    }
  }, []);
  const print = useCallback(() => setPrinting(true), []);

  useEffect(() => {
    const on = () => setFull(document.fullscreenElement === root.current);
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);
  // Leaving the presentation also leaves full screen.
  useEffect(() => () => void (document.fullscreenElement && document.exitFullscreen().catch(() => undefined)), []);

  useEffect(() => {
    root.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key;
      if (["ArrowRight", "ArrowDown", "PageDown", " ", "Enter"].includes(k)) go(at + 1);
      else if (["ArrowLeft", "ArrowUp", "PageUp", "Backspace"].includes(k)) go(at - 1);
      else if (k === "Home") go(0);
      else if (k === "End") go(count - 1);
      else if (k === "o" || k === "O") setOverview((v) => !v);
      else if (k === "n" || k === "N") setNotesShown((v) => !v);
      else if (k === "f" || k === "F") void toggleFull();
      else if (k === "p" || k === "P") print();
      else if (k === "Escape") {
        if (overview) setOverview(false);
        else if (!document.fullscreenElement) onClose();
        // In full screen the browser leaves full screen itself.
      } else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [at, count, go, overview, onClose, toggleFull, print]);

  // In full screen the controls fade out while the mouse rests.
  useEffect(() => {
    if (!full) return setIdle(false);
    let timer = setTimeout(() => setIdle(true), 2500);
    const wake = () => {
      setIdle(false);
      clearTimeout(timer);
      timer = setTimeout(() => setIdle(true), 2500);
    };
    window.addEventListener("mousemove", wake);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", wake);
    };
  }, [full]);

  useEffect(() => {
    if (!printing) return;
    const done = () => setPrinting(false);
    window.addEventListener("afterprint", done);
    // Let the slides render (and their images load) before the print dialog opens.
    const t = setTimeout(() => window.print(), 400);
    return () => {
      clearTimeout(t);
      window.removeEventListener("afterprint", done);
    };
  }, [printing]);

  const controls = (
    <div className={cn("flex shrink-0 flex-wrap items-center gap-1 px-3 py-2 text-sm transition-opacity", full ? "absolute inset-x-0 bottom-0 bg-background/85 backdrop-blur" : "border-b", full && idle && "pointer-events-none opacity-0")}>
      <span className="mr-2 min-w-0 flex-1 truncate font-medium" title={title}>
        {title || "Untitled"}
      </span>
      <Button variant="ghost" size="icon-sm" onClick={() => go(at - 1)} disabled={at === 0} aria-label="Previous slide" title="Previous slide (←)">
        <ChevronLeft />
      </Button>
      <span className="min-w-14 text-center text-xs tabular-nums text-muted-foreground" aria-live="polite">
        {count ? `${at + 1} / ${count}` : "0 / 0"}
      </span>
      <Button variant="ghost" size="icon-sm" onClick={() => go(at + 1)} disabled={at >= count - 1} aria-label="Next slide" title="Next slide (→ or Space)">
        <ChevronRight />
      </Button>
      <Button variant="ghost" size="icon-sm" onClick={() => setOverview((v) => !v)} aria-pressed={overview} aria-label="All slides" title="All slides (O)">
        <LayoutGrid />
      </Button>
      {!full && (
        <Button variant="ghost" size="icon-sm" onClick={() => setNotesShown((v) => !v)} aria-pressed={notesShown} aria-label="Speaker notes" title="Speaker notes (N)">
          <StickyNote />
        </Button>
      )}
      {!full && (
        <Button variant="ghost" size="sm" onClick={() => useUi.getState().setFocusMode(!focusMode)} title="Hide or show the side bars">
          {focusMode ? <PanelLeftOpen /> : <PanelLeftClose />} {focusMode ? "Show side bars" : "Hide side bars"}
        </Button>
      )}
      <Button variant="ghost" size="sm" onClick={() => void toggleFull()} title="Full screen (F)">
        {full ? <Minimize /> : <Maximize />} {full ? "Leave full screen" : "Full screen"}
      </Button>
      <Button variant="ghost" size="icon-sm" onClick={print} aria-label="Print or save as PDF" title="Print or save as PDF, one slide per page (P)">
        <Printer />
      </Button>
      <Button variant="ghost" size="sm" onClick={onClose} title="End the presentation (Esc)">
        <X /> End
      </Button>
    </div>
  );

  return (
    <div ref={root} tabIndex={-1} className={cn("relative flex min-h-0 flex-1 flex-col bg-muted/40 outline-none", full && "bg-black")} role="region" aria-label="Presentation" aria-roledescription="slides">
      {controls}
      {!count ? (
        <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
          This note is empty. Write slides separated by a line with ---, or titles (# or ##): each one starts a slide.
        </div>
      ) : overview ? (
        <ol className="grid min-h-0 flex-1 auto-rows-max grid-cols-[repeat(auto-fill,minmax(14rem,1fr))] gap-4 overflow-y-auto p-4" aria-label="All slides">
          {slides.map((s, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => {
                  go(i);
                  setOverview(false);
                }}
                className={cn("block w-full rounded-md text-left outline-offset-2", i === at && "outline-2 outline-primary")}
                aria-label={`Slide ${i + 1}${s.title ? `: ${s.title}` : ""}`}
              >
                <Scaled className="aspect-video w-full">
                  <SlideCanvas slide={s} first={i === 0} />
                </Scaled>
                <span className="mt-1 block truncate text-xs text-muted-foreground">
                  {i + 1}. {s.title}
                </span>
              </button>
            </li>
          ))}
        </ol>
      ) : (
        <>
          <div className="flex min-h-0 flex-1 cursor-pointer p-4 sm:p-6" onClick={() => go(at + 1)} aria-label={`Slide ${at + 1} of ${count}${slide.title ? `: ${slide.title}` : ""}`} role="group">
            <Scaled className="h-full w-full">
              <SlideCanvas slide={slide} first={at === 0} />
            </Scaled>
          </div>
          {!full && notesShown && slide.notes && (
            <aside className="max-h-40 shrink-0 overflow-y-auto border-t bg-background px-4 py-2 text-sm" aria-label="Speaker notes">
              <p className="mb-1 text-xs font-semibold tracking-wider text-muted-foreground uppercase">Speaker notes</p>
              <MarkdownPreview markdown={slide.notes} />
            </aside>
          )}
        </>
      )}
      {printing &&
        createPortal(
          <div className="print-deck" aria-hidden>
            {slides.map((s, i) => (
              <div key={i} className="print-slide">
                <SlideCanvas slide={s} first={i === 0} />
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
